import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { useAuth } from 'auth';
import { useConfig } from 'config';
import { useFetch } from 'hooks';
import LoadingContainer from 'context/Loading';
import { TerminologyConfigType, DataContextType } from './types';
import { ToastContext, ToastContextProps } from 'toast';
import { ToastType } from 'components/molecules/toast/types';
import { UserSettings } from './types';
import { Country } from './types';
import { useHistory } from 'react-router-dom';

export const DataContext = createContext<DataContextType>({
  userName: '',
  email: '',
  openTAC: false,
  hasPermission: () => false,
  hasSubscription: () => false,
  terminology: {
    thirdPartySingularNaming: 'Third party',
    thirdPartyPluralNaming: 'Third parties',
    internalOwnerSingularNaming: 'Internal owner',
    internalOwnerPluralNaming: 'Internal owners',
    internalIdSingularNaming: 'Internal ID',
  },
});

export const DataProvider = ({ children }) => {
  const history = useHistory();
  const { tokenInfo } = useAuth();
  const { vantageWebApi } = useConfig();
  const { openToast } = useContext<ToastContextProps>(ToastContext);
  const [openTAC, setOpenTAC] = useState<boolean>(false);
  const headers = useMemo(
    () => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${tokenInfo?.accessToken}`,
    }),
    [tokenInfo?.accessToken]
  );

  const {
    data: userData,
    loading,
    error,
    trigger: fetchUserSettings,
  } = useFetch<UserSettings>(`${vantageWebApi}/user/settings`, {
    lazy: true,
    method: 'GET',
    headers,
    onSuccess(data: UserSettings) {
      setOpenTAC(
        !data.isTermsAndConditionsAccepted ||
          data.authenticationMethod != tokenInfo?.authenticationMethod
      );
    },
  });

  /**
   * Checks if the current user has a specific permission.
   * @param {number} permission - The permission to check.
   * @returns {boolean} True if the user has the permission, false otherwise.
   */
  function hasPermission(permission: number): boolean {
    return userData?.permissions ? userData.permissions.includes(permission) : false;
  }

  /**
   * Checks if the current user has a specific subscription.
   * @param {number} subscription - The subscription to check.
   * @returns {boolean} True if the user has the subscription, false otherwise.
   */
  function hasSubscription(subscription: number): boolean {
    return userData?.subscriptions ? userData.subscriptions.includes(subscription) : false;
  }

  /**
   * Save current user decision regarding the T&C (Terms And Conditions)
   * @returns {boolean} True if the user has been accepted, false otherwise.
   */
  const { trigger: postTACFetchLazy, loading: loadingPostTAC } = useFetch(
    `${vantageWebApi}/user/termsandconditions`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${tokenInfo?.accessToken}`,
      },
      lazy: true,
      onSuccess(data: boolean) {
        if (data) {
          setOpenTAC(!data);
          openToast({
            type: ToastType.Success,
            message: 'Saved Successfully!',
          });
          history.push(`/`);
        }
      },
      onError() {
        setOpenTAC(true);
        openToast({
          type: ToastType.Error,
          message: 'Sorry, an error ocurred during the request. Try Again!',
        });
      },
    }
  );

  const {
    loading: terminologyLoading,
    error: terminologyError,
    data: terminologyConfig,
    trigger: fetchNamingConventions,
  } = useFetch<TerminologyConfigType>(`${vantageWebApi}/client/naming-conventions`, {
    lazy: true,
    method: 'GET',
    headers,
  });

  const {
    data: countriesData,
    loading: countriesLoading,
    error: countriesError,
    trigger: fetchCountries,
  } = useFetch<Country[]>(`${vantageWebApi}/client/countries`, {
    lazy: true,
    method: 'GET',
    headers,
  });

  useEffect(() => {
    if (tokenInfo && !terminologyConfig) {
      fetchNamingConventions();
    }
    if (tokenInfo && !countriesData) {
      fetchCountries();
    }
    if (tokenInfo && !userData) {
      fetchUserSettings();
    }
    // eslint-disable-next-line
  }, [tokenInfo]);

  const isLoading = loading || terminologyLoading || countriesLoading || !userData;
  const isError = error || terminologyError || countriesError;
  return (
    <DataContext.Provider
      value={{
        userName: userData?.fullName,
        email: userData?.email,
        gatewayUserId: userData?.id,
        clientId: userData?.clientId,
        openTAC,
        postTACFetchLazy,
        loadingPostTAC,
        isTACAccepted: userData?.isTermsAndConditionsAccepted,
        isEnhancedDocumentUploadControlsEnabled: userData?.isEnhancedDocumentUploadControlsEnabled,
        hasSubscription,
        hasPermission,
        terminology: terminologyConfig,
        countries: countriesData,
        isLinkedThirdpartiesFeatureEnabled: userData?.isLinkedThirdpartiesFeatureEnabled,
      }}
    >
      {isLoading || isError ? <LoadingContainer error={error} /> : children}
    </DataContext.Provider>
  );
};

export const useDataContext = () => useContext(DataContext);

export const useUserSettings = () => {
  const context = useContext(DataContext);
  return context;
};

export const useTerminologyConfig = () => {
  const context = useContext(DataContext);
  return context.terminology;
};

export const useCountries = () => {
  const context = useContext(DataContext);
  return context.countries;
};
